import React, { FormEventHandler, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import TextInput from 'components/Common/Form/Input/TextInput'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { formToObject } from 'lib/forms/formToObject'
import { checkUserExists, register, sendSignUpOTPCode, validateRegistration } from 'actions/AuthActions'
import TextButton from 'LuxKit/Button/TextButton'
import CheckboxInput from 'LuxKit/Checkbox/CheckboxInput'
import BodyText from 'LuxKit/Typography/BodyText'
import HiddenInput from 'components/Common/Form/Input/HiddenInput'
import PhoneInput from 'components/Common/Form/Input/PhoneInput'
import LegalText from './AccountAccessLegalText'
import config from 'constants/config'
import Group from 'components/utils/Group'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import noop from 'lib/function/noop'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { selectLoggedIn } from 'selectors/accountSelectors'
import { AccountAccessModalResult } from './AccountAccessModal'
import { PendingSignUpOTPData } from './AccountAccessModalContent'
import * as Analytics from 'analytics/analytics'
import GeoContext from 'contexts/geoContext'
import ReCAPTCHA from 'react-google-recaptcha'
import { formatPhoneNumber, sanitisePhoneNumber } from 'lib/format/formatPhoneNumber'
import { isWhitelabelBrand } from 'lib/whitelabels/whitelabels'
import { OptimizelyExperiments } from 'constants/optimizely'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'

interface JoinForm {
  email: string
  password: string
  givenName: string
  firstName: string
  phoneNumber?: App.PhoneNumber
  postcode: string
}

interface Props {
  user: App.User
  onModeChange?: (mode: App.UiAccountModalMode, userId?: string) => void
  onPendingSignUpOTPData?: (data: PendingSignUpOTPData) => void
  dismissable?: boolean
}

function AccountAccessJoin(props: Props) {
  const {
    user,
    onModeChange = noop,
    onPendingSignUpOTPData = noop,
    dismissable,
  } = props

  const intl = useIntl()
  const modalContext = useModalElementContext<AccountAccessModalResult>()
  const firstInput = useRef<HTMLInputElement>(null)
  const loggedIn = useAppSelector(selectLoggedIn)
  const { currentRegionCode } = useContext(GeoContext)
  const error = useAppSelector(state => state.auth.error)
  const registrationValidated = useAppSelector(state => state.auth.registrationValidated)
  const reRef = useRef<ReCAPTCHA>(null)
  const formRef = useRef<HTMLFormElement>(null)
  const [emailChecked, setEmailChecked] = useState<string>('')
  const [pendingOTPData, setPendingOTPData] = useState<PendingSignUpOTPData | null>(null)
  const isCheckingEmailRef = useRef(false)
  const dispatch = useAppDispatch()
  const checkedUser = useAppSelector(state => state.auth.users[emailChecked])

  const croPhoneNumberVerificationOnSignUp = !!useOptimizelyExperiment(OptimizelyExperiments.croPhoneNumberVerificationOnSignUp)

  // it's possible to sign up from an email OR a phone number
  const isEmailSignUp = !!user.email

  const isWhitelabel = isWhitelabelBrand(config.BRAND)

  const registerUser = useCallback(() => {
    if (formRef.current) {
      const joinData = formToObject<JoinForm>(formRef.current)
      const phoneNumber = joinData.phoneNumber
      delete joinData.phoneNumber

      const userPhoneNumber = phoneNumber || user.phoneNumber

      if (croPhoneNumberVerificationOnSignUp && userPhoneNumber) {
        // Store the pending data for after validation succeeds
        const signUpData: PendingSignUpOTPData = {
          ...joinData,
          phone: userPhoneNumber.phone,
          phonePrefix: userPhoneNumber.prefix,
        }
        setPendingOTPData(signUpData)
        // Validate registration data first, OTP will be sent after validation succeeds
        dispatch(validateRegistration({
          ...joinData,
          phone: userPhoneNumber.phone,
          phonePrefix: userPhoneNumber.prefix,
        }))
      } else {
        // email sign up has already validated the user exists, we can go straight to registering
        dispatch(register({
          ...joinData,
          ...(phoneNumber?.phone && {
            phone: phoneNumber.phone,
            phonePrefix: phoneNumber.prefix,
          }),
        }, isEmailSignUp ? 'email' : 'phone'))
      }
    }
  }, [dispatch, isEmailSignUp, croPhoneNumberVerificationOnSignUp, user.phoneNumber])

  const handleCheckedUser = useCallback(() => {
    if (checkedUser?.exists) {
      onModeChange('joinExistingAccount', emailChecked)
    } else {
      registerUser()
    }
  }, [checkedUser, onModeChange, emailChecked, registerUser])

  useEffect(() => {
    if (loggedIn) {
      modalContext?.resolve({ loggedIn: true })
    } else if (checkedUser) {
      isCheckingEmailRef.current = false
      handleCheckedUser()
    } else if (!isCheckingEmailRef.current) {
      // Only reset if we're not actively checking an email
      setEmailChecked('')
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedIn, checkedUser])

  // When registration is validated, send OTP and proceed to joinOTP step
  useEffect(() => {
    if (registrationValidated && pendingOTPData && pendingOTPData.phone && pendingOTPData.phonePrefix) {
      const phoneNumber: App.PhoneNumber = {
        phone: pendingOTPData.phone,
        prefix: pendingOTPData.phonePrefix,
      }
      dispatch(sendSignUpOTPCode(phoneNumber))
      onPendingSignUpOTPData(pendingOTPData)
      onModeChange('joinOTP')
      setPendingOTPData(null)
    }
  }, [registrationValidated, pendingOTPData, dispatch, onPendingSignUpOTPData, onModeChange])

  useEffect(() => {
    Analytics.trackClientEvent({
      subject: 'sign_up_details',
      action: 'impression',
      category: isEmailSignUp ? 'email' : 'phone',
      type: 'operational',
    })
    firstInput.current?.focus()
  }, [isEmailSignUp])

  const onJoin = useCallback<FormEventHandler<HTMLFormElement>>((event) => {
    event.preventDefault()

    if (isEmailSignUp) {
      registerUser()
    } else {
      // registering with a phone number, first we need to validate the email
      const joinData = formToObject<JoinForm>(event.currentTarget)

      if (!checkedUser || emailChecked !== joinData.email) {
        // First check OR email changed: need to verify email
        isCheckingEmailRef.current = true
        setEmailChecked(joinData.email)
        dispatch(checkUserExists({ email: joinData.email, phoneNumber: user.phoneNumber }, reRef.current))
      } else {
        // Email already checked and unchanged: proceed directly
        handleCheckedUser()
      }
    }
  }, [dispatch, isEmailSignUp, registerUser, user, checkedUser, emailChecked, handleCheckedUser])

  const onBack = useCallback(() => onModeChange('login'), [onModeChange])

  const welcomeTitle = intl.formatMessage({
    id: 'grQbcoKDC1',
    defaultMessage: 'Welcome',
    description: 'Modal title for the sign-up form',
  })

  // letsCreateText uses FormattedMessage inline (rich text with <b>)
  const identifier = isEmailSignUp ? user.email : formatPhoneNumber(sanitisePhoneNumber(user.phoneNumber?.phone!), user.phoneNumber?.prefix)

  const firstNamePlaceholder = intl.formatMessage({
    id: 'YpUQZmJfmA',
    defaultMessage: 'First Name',
    description: 'Placeholder for first name input on sign-up form',
  })

  const firstNameRequired = intl.formatMessage({
    id: 'yMCDe33AdF',
    defaultMessage: 'First name is required',
    description: 'Validation error when first name is empty on sign-up',
  })

  const firstNameLabel = intl.formatMessage({
    id: '5Cx+SsiCEH',
    defaultMessage: 'First name',
    description: 'Label for first name input on sign-up form',
  })

  const firstNameInvalid = intl.formatMessage({
    id: 'LOLRLNbafZ',
    defaultMessage: 'Please enter a valid first name',
    description: 'Validation error when first name contains invalid characters',
  })

  const lastNamePlaceholder = intl.formatMessage({
    id: 'oUk8OE5JYp',
    defaultMessage: 'Last Name',
    description: 'Placeholder for last name input on sign-up form',
  })

  const lastNameRequired = intl.formatMessage({
    id: 'qVk59+TBBA',
    defaultMessage: 'Last name is required',
    description: 'Validation error when last name is empty on sign-up',
  })

  const lastNameLabel = intl.formatMessage({
    id: 'hrLOrwSdYa',
    defaultMessage: 'Last name',
    description: 'Label for last name input on sign-up form',
  })

  const lastNameInvalid = intl.formatMessage({
    id: 'BYIvPmEpw5',
    defaultMessage: 'Please enter a valid last name',
    description: 'Validation error when last name contains invalid characters',
  })

  const phoneNumberLabel = intl.formatMessage({
    id: 'oIHbjew3VK',
    defaultMessage: 'Phone number',
    description: 'Label for phone number input on sign-up form',
  })

  const emailAddressLabel = intl.formatMessage({
    id: 'BlTSQxatgV',
    defaultMessage: 'Email address',
    description: 'Label for email input on sign-up form',
  })

  const emailPlaceholder = intl.formatMessage({
    id: 'xZ6MZu0jxH',
    defaultMessage: 'Email',
    description: 'Placeholder for email input on sign-up form',
  })

  const emailRequired = intl.formatMessage({
    id: 'zWXcGFUvx/',
    defaultMessage: 'Email is required',
    description: 'Validation error when email is empty on sign-up',
  })

  const postcodePlaceholder = intl.formatMessage({
    id: 'ePexQHQ5qH',
    defaultMessage: 'Postcode',
    description: 'Placeholder for postcode input on sign-up form',
  })

  const postcodeRequired = intl.formatMessage({
    id: 'T4iwKScc+B',
    defaultMessage: 'Postcode is required',
    description: 'Validation error when postcode is empty on sign-up',
  })

  const postcodeLabel = intl.formatMessage({
    id: 'EqaR2IMuvp',
    defaultMessage: 'Postcode',
    description: 'Label for postcode input on sign-up form',
  })

  const passwordPlaceholder = intl.formatMessage({
    id: 'QaIxE3989C',
    defaultMessage: 'Password',
    description: 'Placeholder for password input on sign-up form',
  })

  const passwordRequired = intl.formatMessage({
    id: 'J/lDXjFMbk',
    defaultMessage: 'Password is required',
    description: 'Validation error when password is empty on sign-up',
  })

  const createPasswordLabel = intl.formatMessage({
    id: '2mOKjEJDBg',
    defaultMessage: 'Create password',
    description: 'Label for password input on sign-up form',
  })

  const createAccountLabel = intl.formatMessage({
    id: 'yvCAml9rrq',
    defaultMessage: 'Create account',
    description: 'Submit button text for sign-up form',
  })

  return (
    <>
      <ModalHeader
        title={welcomeTitle}
        onBackButtonClick={onBack}
        dismissible={dismissable}
      />
      <ModalBody>
        <ModalContent>
          <form name="joinForm" onSubmit={onJoin} ref={formRef}>
            <Group direction="vertical" gap={12}>
              <Group direction="vertical" gap={16}>
                {error?.message && <MessageBanner kind="critical">{error.message}</MessageBanner>}
                <BodyText variant="medium">
                  <FormattedMessage
                    id="vKHK9wvlgV"
                    defaultMessage="Let\u2019s create an account for <b>{identifier}</b>"
                    description="Instructional text showing which email or phone number is being registered"
                    values={{
                      identifier,
                      b: (chunks) => <b>{chunks}</b>,
                    }}
                  />
                </BodyText>
                <Group
                  direction="vertical"
                  tabletDirection="horizontal"
                  tabletHorizontalAlign="stretch"
                  gap={12}
                >
                  <TextInput
                    id="givenName"
                    required
                    type="text"
                    name="givenName"
                    placeholder={firstNamePlaceholder}
                    autoComplete="given-name"
                    requiredErrorMessage={firstNameRequired}
                    ref={firstInput}
                    label={firstNameLabel}
                    pattern="^(?=.*[^\s])[^\.\$\%\&\(\)\*!,@#\d]*$"
                    invalidErrorMessage={firstNameInvalid}
                    minLength={2}
                  />
                  <TextInput
                    id="surname"
                    required
                    type="text"
                    name="surname"
                    placeholder={lastNamePlaceholder}
                    autoComplete="family-name"
                    requiredErrorMessage={lastNameRequired}
                    label={lastNameLabel}
                    pattern="^(?=.*[^\s])[^\.\$\%\&\(\)\*!,@#\d]*$"
                    invalidErrorMessage={lastNameInvalid}
                    minLength={2}
                  />
                </Group>
              </Group>
              {isEmailSignUp && <>
                <HiddenInput
                  id="email"
                  name="email"
                  value={user.email}
                />
                <PhoneInput
                  name="phoneNumber"
                  label={phoneNumberLabel}
                  required={isWhitelabel || !!croPhoneNumberVerificationOnSignUp}
                  prefixCountry={currentRegionCode}
                />
              </>}
              {!isEmailSignUp && <>
                <TextInput
                  label={emailAddressLabel}
                  id="email"
                  required
                  type="email"
                  name="email"
                  autoComplete="email"
                  placeholder={emailPlaceholder}
                  requiredErrorMessage={emailRequired}
                  defaultValue={user.email}
                />
                <HiddenInput name="phoneNumber.phone" value={user.phoneNumber?.phone} />
                <HiddenInput name="phoneNumber.prefix" value={user.phoneNumber?.prefix} />
              </>}
              {config.signUp.showPostcode && (
                <TextInput
                  id="postcode"
                  required
                  type="text"
                  name="postcode"
                  placeholder={postcodePlaceholder}
                  autoComplete="off"
                  requiredErrorMessage={postcodeRequired}
                  label={postcodeLabel}
                />
              )}
              {/* Chrome was autofilling the field above password with a username.
              This HiddenInput is a hacky way to prevent that.
              https://stackoverflow.com/questions/23156578/google-chrome-autofilling-all-password-inputs */}
              <HiddenInput type="text" />
              <TextInput
                id="password"
                required
                type="password"
                name="password"
                placeholder={passwordPlaceholder}
                minLength={8}
                autoComplete="new-password"
                requiredErrorMessage={passwordRequired}
                label={createPasswordLabel}
              />
              <CheckboxInput
                name="agreeTandC"
                data-testid="terms-and-privacy-agree-checkbox"
                required
              >
                <LegalText />
              </CheckboxInput>
              <TextButton
                kind="primary"
                fit="flex"
                type="submit"
                size="large"
              >
                {createAccountLabel}
              </TextButton>
            </Group>
          </form>
          {config.RECAPTCHA_KEY && <ReCAPTCHA size="invisible" sitekey={config.RECAPTCHA_KEY} ref={reRef} />}
        </ModalContent>
      </ModalBody>
    </>
  )
}
export default AccountAccessJoin
